import { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { debounce } from "lodash";

import {
  Container,
  Grid,
  Box,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";

import { IsMobile } from "utils/is-mobile";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import FromToDateField from "components/_refactor/fields/from-to-date";
import InputText from "components/_refactor/text/input";
import useConfirmDialog from "hooks/useConfirmDialog";

import Checkbox from "./components/checkbox";

import {
  setProductFilters,
  setSelectedProducts,
  updateFormData,
  updateRepresentative,
  updateRepresentativeFilters,
} from "redux/reducers/criarVitrineReducer";

import { DEFAULT_REPRESENTATIVE_FILTERS } from "../utils/wizard-helpers";
import { brandOptions } from "../utils/showcase-mappers";

export default function GeneralData() {
  const dispatch = useDispatch();
  const isMobile = IsMobile();

  const formData = useSelector((state) => state.criarVitrine.formData);
  const currentStep = useSelector((state) => state.criarVitrine.currentStep);
  const prevStepRef = useRef(currentStep);

  const debouncedUpdateFormData = useMemo(
    () =>
      debounce((payload) => {
        dispatch(updateFormData(payload));
      }, 400),
    [dispatch],
  );

  useEffect(() => {
    return () => debouncedUpdateFormData.cancel();
  }, [debouncedUpdateFormData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    onSubmit: (values) => {
      dispatch(updateFormData(values));
    },
  });

  const { values, setFieldValue } = formik;

  const syncTextField = (key, value) => {
    setFieldValue(key, value);
    debouncedUpdateFormData({ [key]: value });
  };

  const { confirm, ConfirmDialog } = useConfirmDialog();

  const applyBrandChange = (value) => {
    setFieldValue("brand", value);
    setFieldValue("priceTable", null);
    setFieldValue("priceTables", []);
    setFieldValue("paymentCondition", null);
    setFieldValue("paymentConditions", []);
    setFieldValue("minimumInvoice", 0);
    dispatch(
      updateFormData({
        brand: value,
        priceTable: null,
        priceTables: [],
        paymentCondition: null,
        paymentConditions: [],
        minimumInvoice: 0,
      }),
    );
    dispatch(setSelectedProducts([]));
    dispatch(updateRepresentative([]));
    dispatch(setProductFilters({}));
    dispatch(
      updateRepresentativeFilters({ ...DEFAULT_REPRESENTATIVE_FILTERS }),
    );
  };

  const handleChange = async (key, value) => {
    if (key === "brand") {
      const hasDependentData =
        (values.paymentConditions?.length ?? 0) > 0 ||
        (values.priceTables?.length ?? 0) > 0 ||
        formData.brand?.value != null;

      if (hasDependentData && value?.value !== formData.brand?.value) {
        const confirmed = await confirm(
          "Alterar a marca limpa produtos, representantes e configurações selecionadas. Deseja continuar?",
          {
            title: "Alterar marca",
            confirmLabel: "Continuar",
            cancelLabel: "Cancelar",
          },
        );
        if (!confirmed) return;
      }

      applyBrandChange(value);
      return;
    }

    const textFieldsDebounced = new Set(["name", "description"]);

    if (textFieldsDebounced.has(key)) {
      syncTextField(key, value);
      return;
    }

    setFieldValue(key, value);
    dispatch(updateFormData({ [key]: value }));
  };

  useEffect(() => {
    if (prevStepRef.current === 0 && currentStep !== 0) {
      debouncedUpdateFormData.flush();
      dispatch(updateFormData(values));
    }
    prevStepRef.current = currentStep;
  }, [currentStep, debouncedUpdateFormData, dispatch, values]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={4}
            style={{ marginRight: "32px" }}
          >
            <AutoCompleteField
              options={brandOptions}
              label="Marca"
              optionLabelKey="name"
              optionIdKey="value"
              onChange={(option) => handleChange("brand", option)}
              value={formik.values.brand}
              required={true}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8} xl={6}>
            <Box display="flex">
              <FromToDateField
                label="Período de Vigência"
                disablePrefixLabels={true}
                initialDate={
                  formik.values.untilSoldOut ? "" : formik.values.initialDate
                }
                finalDate={
                  formik.values.untilSoldOut ? "" : formik.values.finalDate
                }
                disabled={formik.values.untilSoldOut}
                setInitialDate={(value) => handleChange("initialDate", value)}
                setFinalDate={(value) => handleChange("finalDate", value)}
                isMobile={isMobile}
                required={!formik.values.untilSoldOut}
                pattern="br"
                validateMinDateToday
                validateDateRange
              />
              <Checkbox
                label="Até finalizar o estoque"
                checked={formik.values.untilSoldOut || false}
                onChange={(event) =>
                  handleChange("untilSoldOut", event.target.checked)
                }
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: "20px" }}>
          <Grid item xs={12} md={6}>
            <InputText
              placeholder="Nome da vitrine"
              onChange={(e) => handleChange("name", e.target.value)}
              required={true}
              value={formik.values.name}
              className="form-control"
              style={{ width: "100%" }}
              label="Nome da vitrine"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: "20px" }}>
          <Grid item xs={12}>
            <Typography variant="h6">Descrição</Typography>
            <TextareaAutosize
              rows={6}
              placeholder="Descrição"
              onChange={(e) => handleChange("description", e.target.value)}
              value={formik.values.description}
              className="form-control"
              style={{ width: "100%", height: "140px" }}
              label="Descrição"
            />
          </Grid>
        </Grid>
      </Container>
      <ConfirmDialog />
    </form>
  );
}
